import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Briefcase, MapPin, DollarSign, Plus } from 'lucide-react';
import { Button, Input, TextArea, SearchSelect } from '../shared';
import DateTimePicker from '../shared/DateTimePicker';

const CreateJobPostingModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  job = null, 
  isEdit = false, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    description: '',
    requirements: '',
    responsibilities: '',
    location: '',
    employment_type: '',
    salary_min: '',
    salary_max: '',
    salary_currency: 'GHS',
    is_remote: false,
    is_active: true,
    application_deadline: '',
    sort_order: 0,
    benefits: []
  });

  const [errors, setErrors] = useState({});
  const [benefitInput, setBenefitInput] = useState('');

  const departmentOptions = [
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Design', label: 'Design' },
    { value: 'Operations', label: 'Operations' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Sales', label: 'Sales' },
    { value: 'Analytics', label: 'Analytics' },
    { value: 'HR', label: 'HR' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Support', label: 'Support' }
  ];

  const employmentTypeOptions = [
    { value: 'Full-time', label: 'Full-time' },
    { value: 'Part-time', label: 'Part-time' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Internship', label: 'Internship' },
    { value: 'Freelance', label: 'Freelance' }
  ];

  const currencyOptions = [
    { value: 'GHS', label: 'GHS (Ghana Cedis)' },
    { value: 'USD', label: 'USD (US Dollars)' },
    { value: 'EUR', label: 'EUR (Euros)' }
  ];

  useEffect(() => {
    if (isEdit && job) {
      setFormData({
        title: job.title || '',
        department: job.department || '',
        description: job.description || '',
        requirements: job.requirements || '',
        responsibilities: job.responsibilities || '',
        location: job.location || '',
        employment_type: job.employment_type || '',
        salary_min: job.salary_min || '',
        salary_max: job.salary_max || '',
        salary_currency: job.salary_currency || 'GHS',
        is_remote: job.is_remote || false,
        is_active: job.is_active ?? true,
        application_deadline: job.application_deadline ? new Date(job.application_deadline).toISOString().slice(0, 16) : '',
        sort_order: job.sort_order || 0,
        benefits: job.benefits || []
      });
    } else {
      setFormData({
        title: '',
        department: '',
        description: '',
        requirements: '',
        responsibilities: '',
        location: '',
        employment_type: '',
        salary_min: '',
        salary_max: '',
        salary_currency: 'GHS',
        is_remote: false,
        is_active: true,
        application_deadline: '',
        sort_order: 0,
        benefits: []
      });
    }
    setErrors({});
    setBenefitInput('');
  }, [isEdit, job, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAddBenefit = () => {
    if (benefitInput.trim() && !formData.benefits.includes(benefitInput.trim())) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, benefitInput.trim()]
      }));
      setBenefitInput('');
    }
  };

  const handleRemoveBenefit = (index) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.department.trim()) {
      newErrors.department = 'Department is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.employment_type.trim()) {
      newErrors.employment_type = 'Employment type is required';
    }

    if (formData.salary_min && formData.salary_min < 0) {
      newErrors.salary_min = 'Minimum salary must be positive';
    }

    if (formData.salary_max && formData.salary_max < 0) {
      newErrors.salary_max = 'Maximum salary must be positive';
    }

    if (formData.salary_min && formData.salary_max && parseFloat(formData.salary_min) > parseFloat(formData.salary_max)) {
      newErrors.salary_max = 'Maximum salary must be greater than minimum salary';
    }

    if (formData.application_deadline && new Date(formData.application_deadline) < new Date()) {
      newErrors.application_deadline = 'Application deadline must be in the future';
    }

    if (formData.sort_order < 0) {
      newErrors.sort_order = 'Sort order must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
      salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
      application_deadline: formData.application_deadline 
        ? new Date(formData.application_deadline).toISOString() 
        : null,
      sort_order: parseInt(formData.sort_order) || 0,
      benefits: formData.benefits.length > 0 ? formData.benefits : null
    };

    onSave(submitData);
  };

  const handleClose = () => {
    setErrors({});
    setBenefitInput('');
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto relative my-2 sm:my-4">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center">
              <Briefcase className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
            </div>
            <h3 className="ml-2 sm:ml-3 text-base sm:text-lg font-medium text-gray-900 truncate">
              {isEdit ? 'Edit Job Posting' : 'Create Job Posting'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title <span className="text-red-500">*</span>
              </label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Senior Full-Stack Developer"
                error={errors.title}
                required
              />
            </div>

            {/* Department and Employment Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department <span className="text-red-500">*</span>
                </label>
                <SearchSelect
                  value={formData.department}
                  onChange={(value) => handleSelectChange('department', value)}
                  options={departmentOptions}
                  placeholder="Select department"
                  error={errors.department}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employment Type <span className="text-red-500">*</span>
                </label>
                <SearchSelect
                  value={formData.employment_type}
                  onChange={(value) => handleSelectChange('employment_type', value)}
                  options={employmentTypeOptions}
                  placeholder="Select employment type"
                  error={errors.employment_type}
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., Remote / Accra, Ghana"
                  error={errors.location}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the role, responsibilities, and what you're looking for..."
                rows={6}
                error={errors.description}
                required
              />
            </div>

            {/* Requirements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requirements
              </label>
              <TextArea
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                placeholder="List the required skills, experience, and qualifications..."
                rows={6}
                error={errors.requirements}
              />
            </div>

            {/* Responsibilities */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsibilities
              </label>
              <TextArea
                name="responsibilities"
                value={formData.responsibilities}
                onChange={handleChange}
                placeholder="List the key responsibilities and duties..."
                rows={6}
                error={errors.responsibilities}
              />
            </div>

            {/* Salary Range */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Salary
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    name="salary_min"
                    type="number"
                    value={formData.salary_min}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    error={errors.salary_min}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Salary
                </label>
                <Input
                  name="salary_max"
                  type="number"
                  value={formData.salary_max}
                  onChange={handleChange}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  error={errors.salary_max}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <SearchSelect
                  value={formData.salary_currency}
                  onChange={(value) => handleSelectChange('salary_currency', value)}
                  options={currencyOptions}
                />
              </div>
            </div>

            {/* Application Deadline and Sort Order */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <DateTimePicker
                  label="Application Deadline"
                  value={formData.application_deadline}
                  onChange={(value) => {
                    const dateStr = value ? new Date(value).toISOString().slice(0, 16) : '';
                    setFormData(prev => ({ ...prev, application_deadline: dateStr }));
                    if (errors.application_deadline) {
                      setErrors(prev => ({ ...prev, application_deadline: '' }));
                    }
                  }}
                  placeholder="Select deadline date and time"
                  allowPastDates={false}
                  minDate={new Date().toISOString()}
                  error={errors.application_deadline}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order
                </label>
                <Input
                  name="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={handleChange}
                  placeholder="0"
                  min="0"
                  error={errors.sort_order}
                />
              </div>
            </div>

            {/* Benefits */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Benefits
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={benefitInput}
                  onChange={(e) => setBenefitInput(e.target.value)}
                  placeholder="Add a benefit"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddBenefit();
                    }
                  }}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={handleAddBenefit}
                  variant="outline"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.benefits.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.benefits.map((benefit, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      {benefit}
                      <button
                        type="button"
                        onClick={() => handleRemoveBenefit(index)}
                        className="hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Checkboxes */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_remote"
                  checked={formData.is_remote}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Remote Position</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              className="w-full sm:w-auto"
            >
              {isEdit ? 'Update Job Posting' : 'Create Job Posting'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CreateJobPostingModal;

